import React, {useContext, useMemo, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {IFormFieldItemPropsContext} from '@src/@interfaces/contexts/IFormFieldItemPropsContext';
import {IOrderModel} from '@src/@interfaces/models/Order/IOrderModel';
import {FormFieldItemPropsContext} from '@src/contexts/FormFieldItemPropsContext';
import MessageReceiver, {
    makeOnDeleteAttachListener,
    makeOnDropAttachListener
} from '@src/components/Chat/MessageReceiver/MessageReceiver';
import AttachmentsPreviews from '@src/components/Chat/AttachmentsPreviews/AttachmentsPreviews';
import Translate from '@src/components/Translate/Translate';
import app from '@src/helpers/app';
import {refetchAllOrderQueriesExceptDisabled} from '@views/orders/refetchAllOrderQueriesExceptDisabled';
import classnames from '@src/helpers/classnames';

export default function StatusJudgeApproval(props: {
    orderId: string | number,
    orderProductId: string | number,
    controllerName: string,
    placeholder?: string,
    isDisabled?: boolean,
}) {
    const {
        forceDisabled,
        forceReadonly
    } = useContext<IFormFieldItemPropsContext<IOrderModel> | null>(FormFieldItemPropsContext) ?? {};

    const [attachments, setAttachments] = useState<File[]>([]);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const queryClient = useQueryClient();

    const [onDelete, onDrop] = useMemo(() => {
        return [
            makeOnDeleteAttachListener(setAttachments),
            makeOnDropAttachListener(setAttachments),
        ];
    }, [setAttachments]);

    const makeRequest = (routeName: string) => {
        if (props.isDisabled) {
            return;
        }

        return () => app('http').route(routeName, {
            id: String(props.orderId),
            product: String(props.orderProductId),
        }, {
            headers: {'content-type': 'multipart/form-data'},
            body: {'controller-name': props.controllerName, message, attachments}
        }).then(() => {
            return refetchAllOrderQueriesExceptDisabled(
                    queryClient,
                    app('queryKeys').getAllQueriesOfExactModelQueryKey(
                            props.controllerName,
                            props.orderId
                    )
            ).then(() => true);
        }).catch(e => {
            setError(e.message);
        });
    };

    return <>
        {error ? (
                <div className="alert alert-danger alert-dismissible px-2 py-1">
                    <Translate message={error}/>

                    <button
                            type="button"
                            className="btn btn-close p-2"
                            onClick={() => setError(null)}
                            disabled={props.isDisabled}
                    />
                </div>
        ) : null}

        <MessageReceiver
                onInput={setMessage}
                onDrop={onDrop}
                defaultValue={message}
                isDisabled={props.isDisabled || forceDisabled || forceReadonly}
                placeholder={props.placeholder ?? 'CommentList.message'}
                filesPreviewSlot={
                    <AttachmentsPreviews attachment={attachments} onDelete={onDelete}/>
                }
        />

        <div className="text-end mt-2">
            <Translate<HTMLButtonElement>
                    type="button"
                    message="OrderForm.products.status_approval.decline"
                    className={classnames('btn btn-sm btn-outline-danger me-2', {
                        'disabled': props.isDisabled
                    })}
                    onClick={makeRequest('orders.products.decline-approval')}
                    disabled={props.isDisabled}
            />

            <Translate<HTMLButtonElement>
                    type="button"
                    message="OrderForm.products.status_approval.accept"
                    className={classnames('btn btn-sm btn-outline-success', {
                        'disabled': props.isDisabled
                    })}
                    onClick={makeRequest('orders.products.accept-approval')}
                    disabled={props.isDisabled}
            />
        </div>
    </>;
}
